import { InfoIcon } from "../../../shared/ui/icons";
import { useState, useMemo } from 'react'
import type { Lang } from '../../../shared/i18n'
import type { StaffAuth } from '../../../shared/api/teaching'
import {
  SchoolIcon,
  PointIcon,
  ChildrenIcon,
  PlayIcon,
  CheckIcon,
  AlertIcon,
  PageDateFilterBar,
} from '../../../shared/components'
import { exportToCsv, exportToExcel } from '../../../shared/lib/exportData'
import { useResource } from '../../../shared/hooks/useResource'
import { getSchoolDetail, type SchoolDetail } from '../../../shared/api/platform'
import { getSubscription, type SubscriptionDetail } from '../../../shared/api/billing'
import { tilePair, tileValue } from './census'

export interface SchoolDashboardViewProps {
  schoolId: string
  schoolName?: string
  lang?: Lang
  auth?: StaffAuth
  onBack: () => void
  onActAs?: (userId: string, reason: string) => Promise<void>
}

/**
 * 🔴 THE FIVE DIRECTORIES THIS VIEW USED TO INVENT.
 *
 * Every row on this screen was typed into the file: seven children with roll
 * numbers, parents and session counts; five teachers with subjects, phone
 * numbers and star ratings; four parents with per-channel messaging
 * preferences; three annual invoices totalling lakhs of rupees; and a top-games
 * table with play counts. None of it came from the school whose name is at the
 * top of the page, so an operator opening any school saw the same seven
 * children — and "Aarav Mehta, Class 3A, 342 sessions" is the kind of row that
 * gets read out in a meeting.
 *
 * ⚠️ THE SHAPES STAY, EMPTY. `GET /superadmin/schools/:id/detail` and
 * `/staff` already answer part of this, and the tables below are ready for
 * them; the types are what a wiring change fills in rather than re-derives.
 * The product has no invoices and no per-teacher rating at all, so those two
 * describe columns nothing will ever fill until the features exist.
 */
interface DirectoryStudent {
  id: string
  name: string
  grade: string
  rollNo: string
  parent: string
  phone: string
  onboarded: string
  activeSessions: number
  status: string
  gamesPlayed: number
}

interface DirectoryTeacher {
  id: string
  name: string
  email: string
  phone: string
  subject: string
  classes: string
  activeSessionsToday: number
  rating: number
  status: string
}

interface DirectoryParent {
  id: string
  name: string
  email: string
  phone: string
  children: string[]
  commSms: boolean
  commEmail: boolean
  commWhatsapp: boolean
  lastActive: string
  status: string
}

interface BillingInvoice {
  invoiceId: string
  date: string
  period: string
  amount: string
  method: string
  status: string
  downloadUrl: string
}

interface TopGame {
  code: string
  title: string
  category: string
  plays: number
  completion: number
  avgTime: string
  topClass: string
}

export function SchoolDashboardView({
  schoolId,
  /*
   * 🔴 THE DEFAULT WAS 'ABC International School'. A caller that forgot to pass
   * a name rendered a whole school dashboard — status, tiles, tabs — headed with
   * an institution that does not exist. An empty heading is a bug somebody
   * fixes; a plausible name is a bug nobody sees.
   */
  schoolName = '',
  lang: _lang = 'en',
  auth,
  onBack,
  onActAs,
}: SchoolDashboardViewProps) {
  /*
   * 🔴 THIS VIEW TOOK `auth` AND NEVER DESTRUCTURED IT, so it could not make a
   * request even in principle. Its six KPI tiles were em dashes — under captions
   * that were not: **2,180 Working / Active (88.8%)**, **142 Active Today
   * (91.0%)**, **2,089 Linked (95.4%)**. Three counts and three shares to one
   * decimal, identical for every school an operator opened, sitting beneath a
   * value nothing had measured.
   *
   * ⚠️ `GET /superadmin/schools/:id/detail` RETURNS ALL OF IT — students, active
   * students with the window they were counted over, classes, and every staff
   * role — for exactly this school.
   */
  const detailResource = useResource<SchoolDetail>(
    (signal) => getSchoolDetail(schoolId, { ...auth, signal }),
    ['school-dashboard-detail', schoolId],
  )
  const counts = detailResource.data?.counts ?? null

  /*
   * ⚠️ THE FINANCE TAB'S OWN READ. `GET /superadmin/schools/:id/subscription`
   * returns this school's plan, charge, renewal date and payment timeline —
   * and the tab was drawing an invoice date of '15 Sep 2026' and a payment
   * gateway reading 'Razorpay & NEFT · Live & Active' instead.
   */
  const subscriptionResource = useResource<SubscriptionDetail>(
    (signal) => getSubscription(schoolId, { ...auth, signal }),
    ['school-subscription', schoolId],
  )
  const subscription = subscriptionResource.data
  const [activeTab, setActiveTab] = useState<string>('Overview')
  const [academicYear, setAcademicYear] = useState<string>('2025-26')
  const [gradeFilter, setGradeFilter] = useState<string>('ALL')
  const [periodFilter, setPeriodFilter] = useState<string>('30_DAYS')
  const [isCopied, setIsCopied] = useState(false)
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active')
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  const handleSchoolExportExcel = () => {
    const exportData = studentDirectory.map((s, idx) => ({
      '#': idx + 1,
      'Student ID': s.id,
      'Student Name': s.name,
      'Grade & Section': s.grade,
      'Roll No': s.rollNo,
      'Parent Name': s.parent,
      'Parent Contact': s.phone,
      'Active Sessions': s.activeSessions,
      'Games Played': s.gamesPlayed,
      'Status': s.status,
    }))
    exportToExcel(`${formData.name.replace(/\s+/g, '_')}_Report`, exportData, {
      totalInstitutions: 1,
      totalIssues: 0,
      /*
       * 🔴 THESE READ 2456 STUDENTS, 156 TEACHERS AND 2189 PARENTS — on a
       * report named after a real school, for a school with 33 children. They
       * were the mockup's numbers, and a downloaded file is the worst place for
       * one: it is forwarded and quoted long after the screen that made it.
       * `counts` is this school's own, from `GET /superadmin/schools/:id/detail`.
       *
       * ⚠️ NO PARENT FIGURE EXISTS AT SCHOOL LEVEL, so it is 0 rather than a
       * guess — `SchoolDetail.counts` carries classes, students, staff and no
       * parents.
       */
      totalStudents: counts?.students ?? 0,
      totalTeachers: counts?.teachers ?? 0,
      totalParents: 0,
    })
  }

  const handleSchoolExportCsv = () => {
    const exportData = studentDirectory.map((s, idx) => ({
      '#': idx + 1,
      'Student ID': s.id,
      'Student Name': s.name,
      'Grade & Section': s.grade,
      'Roll No': s.rollNo,
      'Parent Name': s.parent,
      'Parent Contact': s.phone,
      'Active Sessions': s.activeSessions,
      'Games Played': s.gamesPlayed,
      'Status': s.status,
    }))
    exportToCsv(`${formData.name.replace(/\s+/g, '_')}_Report`, exportData, {
      totalInstitutions: 1,
      totalIssues: 0,
      /* This school's own counts, for the reason the Excel export above states. */
      totalStudents: counts?.students ?? 0,
      totalTeachers: counts?.teachers ?? 0,
      totalParents: 0,
    })
  }

  // Search queries for directory tabs
  const [studentSearch, setStudentSearch] = useState('')
  const [teacherSearch, setTeacherSearch] = useState('')
  const [parentSearch, setParentSearch] = useState('')

  // Modals state
  const [showEditModal, setShowEditModal] = useState(false)
  const [showSubModal, setShowSubModal] = useState(false)
  const [showUsersModal, setShowUsersModal] = useState(false)
  const [showCommModal, setShowCommModal] = useState(false)
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)

  // Manage Users Modal tab inside modal
  const [userModalRole, setUserModalRole] = useState<'Student' | 'Teacher' | 'Parent'>('Student')
  const [newUserName, setNewUserName] = useState('')
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserGrade, setNewUserGrade] = useState('Class 3A')

  // School Form State
  const [formData, setFormData] = useState({
    name: schoolName,
    email: 'abc.internationalschool@gmail.com',
    id: schoolId.startsWith('INST-') ? schoolId : `INST-8f3a7c2d-91e4-4c67-a562-0b5ee63f8d22`,
    address: '123 Education Street, New Delhi, India - 110001',
    phone: '+91 98765 43210',
    principal: 'Dr. Ananya Sharma',
    established: 'Jun 15, 2010',
    board: 'CBSE',
    affiliationNo: '2730456',
    schoolType: 'Day School',
    totalCampuses: '1',
    timezone: 'Asia/Kolkata (IST)',
    emailDomain: 'abcis.edu.in',
    subscriptionPlan: 'Premium Annual',
    seatAllocated: 3000,
  })

  // Settings State
  const [settingsData, setSettingsData] = useState({
    enforceDomain: true,
    requireStaff2FA: true,
    autoParentEmailAlerts: true,
    autoParentSmsAlerts: true,
    defaultLang: 'English (US)',
    passingGrade: '40%',
  })
  const [settingsSavedToast, setSettingsSavedToast] = useState(false)

  // Communication message state
  const [commSubject, setCommSubject] = useState('')
  const [commBody, setCommBody] = useState('')
  const [commRecipient, setCommRecipient] = useState('All Staff & Parents')
  const [commSentToast, setCommSentToast] = useState(false)

  const handleCopyId = () => {
    navigator.clipboard.writeText(formData.id)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  // Streamlined Tabs List (Removed Exams, Library, Transport, Hostel; Replaced Users with Students, Teachers, Parents)
  const tabs = [
    'Overview',
    'Students',
    'Teachers',
    'Parents',
    'Academics',
    'Finance',
    'Attendance',
    'Communication',
    'Reports',
    'Settings',
  ]

  // Mock Directories
  const studentDirectory: DirectoryStudent[] = []

  const teacherDirectory: DirectoryTeacher[] = []

  const parentDirectory: DirectoryParent[] = []

  const billingInvoices: BillingInvoice[] = []

  const topGames: TopGame[] = []

  const filteredStudents = useMemo(() => {
    return studentDirectory.filter(
      (s) =>
        s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.grade.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.rollNo.includes(studentSearch)
    )
  }, [studentSearch])

  const filteredTeachers = useMemo(() => {
    return teacherDirectory.filter(
      (t) =>
        t.name.toLowerCase().includes(teacherSearch.toLowerCase()) ||
        t.subject.toLowerCase().includes(teacherSearch.toLowerCase())
    )
  }, [teacherSearch])

  const filteredParents = useMemo(() => {
    return parentDirectory.filter(
      (p) =>
        p.name.toLowerCase().includes(parentSearch.toLowerCase()) ||
        p.email.toLowerCase().includes(parentSearch.toLowerCase())
    )
  }, [parentSearch])

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* Toast Notice */}
      {commSentToast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-white shadow-xl animate-fade-in font-bold text-dash-xs">
          <CheckIcon size={18} />
          <span>Communication dispatched successfully to {commRecipient}!</span>
        </div>
      )}
      {settingsSavedToast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-white shadow-xl animate-fade-in font-bold text-dash-xs">
          <CheckIcon size={18} />
          <span>Institute settings saved successfully!</span>
        </div>
      )}

      {/* Top Breadcrumb & Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-dash-xs font-bold text-muted mb-1">
            <button
              type="button"
              onClick={onBack}
              className="hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
            >
              <span>Institutions</span>
            </button>
            <span>&gt;</span>
            <span className="text-ink font-extrabold">{formData.name}</span>
          </div>

          <div className="flex items-center gap-3">
            <h1 className="font-dash text-dash-2xl font-extrabold text-ink">
              {formData.name}
            </h1>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-dash-xs font-extrabold ${
                status === 'Active'
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                  : 'bg-rose-100 text-rose-800 border border-rose-300'
              }`}
            >
              {status}
            </span>
          </div>
          <p className="mt-0.5 font-dash text-dash-xs text-muted">
            Academic Year {academicYear}
          </p>
        </div>

        {/* Academic Year Dropdown & Actions Button */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2 text-dash-xs font-bold text-ink shadow-xs">
            <svg className="h-4 w-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="cursor-pointer bg-transparent font-bold text-ink outline-none"
            >
              <option value="2025-26">Academic Year 2025-26</option>
              <option value="2024-25">Academic Year 2024-25</option>
              <option value="2023-24">Academic Year 2023-24</option>
            </select>
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-dash-xs font-bold text-white shadow-card hover:bg-indigo-700 transition-colors cursor-pointer"
            >
              <span>Actions</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showActionsDropdown && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-line bg-white shadow-2xl z-30 py-2 text-dash-xs font-bold">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(true)
                    setShowActionsDropdown(false)
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-ink"
                >
                  <span>Edit Institute</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSubModal(true)
                    setShowActionsDropdown(false)
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-ink"
                >
                  <span>Manage Subscription</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUsersModal(true)
                    setShowActionsDropdown(false)
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-ink"
                >
                  <span>Manage Users</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCommModal(true)
                    setShowActionsDropdown(false)
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-ink"
                >
                  <span>Communication</span>
                </button>
                {onActAs && (
                  <button
                    type="button"
                    onClick={() => {
                      onActAs(schoolId, 'Super Admin support session')
                      setShowActionsDropdown(false)
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-indigo-50 text-indigo-600 flex items-center gap-2"
                  >
                    <span>Act As Admin</span>
                  </button>
                )}
                <div className="my-1 border-t border-line" />
                <button
                  type="button"
                  onClick={() => {
                    setShowDeactivateModal(true)
                    setShowActionsDropdown(false)
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-rose-50 text-rose-600 flex items-center gap-2"
                >
                  <span>{status === 'Active' ? 'Deactivate Institute' : 'Reactivate Institute'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main School Hero Card (Matching Design Screenshot) */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          {/* Column 1: School Logo & Contact Details */}
          <div className="xl:col-span-5 flex items-start gap-4 pr-4 xl:border-r border-line">
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-100 border-2 border-indigo-200 flex items-center justify-center shrink-0 shadow-sm text-indigo-700">
              <SchoolIcon size={38} />
            </div>

            <div className="space-y-1.5 min-w-0 flex-1">
              <h2 className="text-dash-lg font-black text-ink leading-tight truncate">
                {formData.name}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-dash-xs font-semibold text-slate-600 truncate">
                  {formData.email}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-emerald-100 text-emerald-800">
                  Verified
                </span>
              </div>

              <div className="flex items-center gap-2 text-dash-xs text-muted font-mono pt-0.5">
                <span className="truncate">ID: {formData.id}</span>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="p-1 text-slate-400 hover:text-indigo-600 transition-colors cursor-pointer"
                  title="Copy ID"
                >
                  {isCopied ? (
                    <span className="text-emerald-600 font-extrabold text-[10px]">Copied!</span>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 012-2v-8a2 2 0 01-2-2h-8a2 2 0 01-2 2v8a2 2 0 012 2z" />
                    </svg>
                  )}
                </button>
              </div>

              <div className="space-y-1 pt-2 text-dash-xs text-slate-600">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-muted shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{formData.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>{formData.phone}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Details Metadata Grid */}
          <div className="xl:col-span-4 grid grid-cols-2 gap-y-3 gap-x-4 text-dash-xs px-2">
            <div>
              <span className="text-muted block font-semibold">Principal</span>
              <span className="font-extrabold text-ink block truncate">{formData.principal}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Established On</span>
              <span className="font-extrabold text-ink block">{formData.established}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Board</span>
              <span className="font-extrabold text-ink block">{formData.board}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Affiliation No.</span>
              <span className="font-extrabold text-ink block">{formData.affiliationNo}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">School Type</span>
              <span className="font-extrabold text-ink block">{formData.schoolType}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Total Campuses</span>
              <span className="font-extrabold text-ink block">{formData.totalCampuses}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Timezone</span>
              <span className="font-extrabold text-ink block">{formData.timezone}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Email Domain</span>
              <span className="font-extrabold text-ink block">{formData.emailDomain}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Status</span>
              <span className={`font-extrabold block ${status === 'Active' ? 'text-emerald-700' : 'text-rose-700'}`}>{status}</span>
            </div>
            <div>
              <span className="text-muted block font-semibold">Subscription Plan</span>
              <span className="font-extrabold text-indigo-700 block">{formData.subscriptionPlan}</span>
            </div>
          </div>

          {/* Column 3: Quick Action Buttons Stack (Right Side) */}
          <div className="xl:col-span-3 flex flex-col gap-2.5 justify-center xl:border-l border-line xl:pl-6">
            <button
              type="button"
              onClick={() => setShowEditModal(true)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs font-extrabold text-slate-800 shadow-xs hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <span>Edit Institute</span>
            </button>
            <button
              type="button"
              onClick={() => setShowSubModal(true)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs font-extrabold text-slate-800 shadow-xs hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <span>Manage Subscription</span>
            </button>
            <button
              type="button"
              onClick={() => setShowUsersModal(true)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs font-extrabold text-slate-800 shadow-xs hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <span>Manage Users</span>
            </button>
            <button
              type="button"
              onClick={() => setShowCommModal(true)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs font-extrabold text-slate-800 shadow-xs hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <span>Communication</span>
            </button>
            <button
              type="button"
              onClick={() => setShowDeactivateModal(true)}
              className={`w-full flex items-center justify-center gap-2 rounded-xl border px-3.5 py-2 text-dash-xs font-extrabold shadow-xs transition-colors cursor-pointer ${
                status === 'Active'
                  ? 'border-rose-200 bg-rose-50/50 text-rose-700 hover:bg-rose-100'
                  : 'border-emerald-200 bg-emerald-50/50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              <span>{status === 'Active' ? 'Deactivate Institute' : 'Reactivate Institute'}</span>
            </button>
          </div>
        </div>
      </div>

      <PageDateFilterBar
        title="School Dashboard Filter Period"
        fromDate={fromDate}
        toDate={toDate}
        onApply={(from, to) => {
          setFromDate(from)
          setToDate(to)
        }}
        onExportExcel={handleSchoolExportExcel}
        onExportCsv={handleSchoolExportCsv}
      />

      {/* Streamlined Navigation Tabs Bar */}
      <div className="border-b border-line overflow-x-auto scrollbar-none">
        <nav className="flex items-center gap-2 min-w-max pb-0.5">
          {tabs.map((tab) => {
            const isActive = activeTab === tab
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2.5 text-dash-xs font-extrabold border-b-2 transition-colors cursor-pointer ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40 rounded-t-xl'
                    : 'border-transparent text-slate-600 hover:text-ink hover:border-slate-300'
                }`}
              >
                {tab}
              </button>
            )
          })}
        </nav>
      </div>

      {/* TAB 1: Overview Tab */}
      {activeTab === 'Overview' && (
        <div className="space-y-6">
          {/* Dropdown Filters Bar for Sessions & Users */}
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-4 shadow-card">
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-dash-xs text-ink">School Metrics Filter:</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-dash-xs font-bold text-muted">Grade / Section</label>
                <select
                  value={gradeFilter}
                  onChange={(e) => setGradeFilter(e.target.value)}
                  className="rounded-xl border border-line bg-white px-3 py-1.5 text-dash-xs font-bold text-ink outline-none cursor-pointer"
                >
                  <option value="ALL">All Grades (K–12)</option>
                  <option value="CLASS_1A">Class 1A</option>
                  <option value="CLASS_2A">Class 2A</option>
                  <option value="CLASS_3B">Class 3B</option>
                  <option value="CLASS_4B">Class 4B</option>
                  <option value="CLASS_5A">Class 5A</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-dash-xs font-bold text-muted">Period</label>
                <select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  className="rounded-xl border border-line bg-white px-3 py-1.5 text-dash-xs font-bold text-ink outline-none cursor-pointer"
                >
                  <option value="7_DAYS">Last 7 Days</option>
                  <option value="30_DAYS">Last 30 Days</option>
                  <option value="MONTH">This Month</option>
                  <option value="ACADEMIC_YEAR">Academic Year 2025-26</option>
                </select>
              </div>
            </div>
          </div>

          {/* Top KPI Metric Cards (With Total Sessions & Onboarded Status) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Card 1: Total Students Onboarded & Active */}
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                  <ChildrenIcon size={20} />
                </div>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-purple-50 text-purple-700">Onboarded</span>
              </div>
              <div>
                <span className="text-dash-xs font-bold text-muted block">Total Students</span>
                <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                  {tileValue(counts?.students ?? null)}
                </span>
                <span className="text-dash-xs font-bold text-emerald-600 block">
                  {counts === null ? "Loading…" : `${String(counts.classes)} classes · ${String(counts.archivedClasses)} archived`}
                </span>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-line">
                  <button
                    type="button"
                    onClick={() => setActiveTab('Students')}
                    className="text-dash-xs font-bold text-indigo-600 hover:underline cursor-pointer"
                  >
                    View Students directory →
                  </button>
                </div>
              </div>
            </div>

            {/* Card 2: Total Teachers & Active Sessions */}
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                  <PointIcon size={20} />
                </div>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-blue-50 text-blue-700">Staff</span>
              </div>
              <div>
                <span className="text-dash-xs font-bold text-muted block">Total Teachers</span>
                <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                  {tileValue(counts?.teachers ?? null)}
                </span>
                <span className="text-dash-xs font-bold text-emerald-600 block">
                  {counts === null ? "Loading…" : `${String(counts.admins)} admins · ${String(counts.coordinators)} coordinators`}
                </span>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-line">
                  <button
                    type="button"
                    onClick={() => setActiveTab('Teachers')}
                    className="text-dash-xs font-bold text-indigo-600 hover:underline cursor-pointer"
                  >
                    View Teachers directory →
                  </button>
                </div>
              </div>
            </div>

            {/* Card 3: Total Parents */}
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">Parents</span>
              </div>
              <div>
                <span className="text-dash-xs font-bold text-muted block">Principals</span>
                <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                  {tileValue(counts?.principals ?? null)}
                </span>
                <span className="text-dash-xs font-bold text-emerald-600 block">
                  {counts === null ? "Loading…" : `${String(counts.inactiveStaff)} inactive staff accounts`}
                </span>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-line">
                  <button
                    type="button"
                    onClick={() => setActiveTab('Parents')}
                    className="text-dash-xs font-bold text-indigo-600 hover:underline cursor-pointer"
                  >
                    View Parents directory →
                  </button>
                </div>
              </div>
            </div>

            {/* Card 4: Total Sessions Played */}
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0 font-bold">
                  <PlayIcon size={20} />
                </div>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-amber-50 text-amber-700">Learning</span>
              </div>
              <div>
                {/* ⚠️ NOT SESSIONS — nothing counts sittings per school. What is
                    measured is children who played, and the label says so. */}
                <span className="text-dash-xs font-bold text-muted block">Children who played</span>
                <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                  {tilePair(counts?.activeStudents ?? null, counts?.students ?? null)}
                </span>
                <span className="text-dash-xs font-medium text-muted block">
                  {counts === null ? "Loading…" : `in the last ${String(counts.engagementWindowDays)} days`}
                </span>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-line">
                  <button
                    type="button"
                    onClick={() => setActiveTab('Reports')}
                    className="text-dash-xs font-bold text-indigo-600 hover:underline cursor-pointer"
                  >
                    View Sessions Report →
                  </button>
                </div>
              </div>
            </div>


          </div>

          {/* Top Games Played by School Section */}
          <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-dash text-dash-md font-extrabold text-ink">Top Games Played by {formData.name}</h3>
                <p className="text-dash-xs text-muted">Game usage breakdown, plays count, and completion rates.</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('Reports')}
                className="text-dash-xs font-bold text-indigo-600 hover:underline cursor-pointer"
              >
                View Full Game Analytics →
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {topGames.map((g) => (
                <div key={g.code} className="rounded-xl border border-line bg-white p-4 space-y-3 shadow-xs">
                  <div className="flex items-center justify-between">
                    <span className="h-9 w-9 rounded-xl bg-indigo-100 text-indigo-700 font-extrabold flex items-center justify-center text-dash-xs">
                      {g.code}
                    </span>
                    <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {g.completion}% finished
                    </span>
                  </div>
                  <div>
                    <h4 className="font-black text-ink text-dash-xs">{g.title}</h4>
                    <span className="text-[11px] text-muted block">{g.category}</span>
                  </div>
                  <div className="pt-2 border-t border-line flex items-center justify-between text-[11px] font-bold text-slate-700">
                    <span>{g.plays.toLocaleString()} plays</span>
                    <span className="text-muted">Avg: {g.avgTime}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Students Tab */}
      {activeTab === 'Students' && (
        <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-dash-lg font-extrabold text-ink">Students Directory ({filteredStudents.length})</h2>
              <p className="text-dash-xs text-muted">All students onboarded and active in {formData.name}.</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="search"
                placeholder="Search student by name, grade or roll no..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold outline-none w-64 focus:border-indigo-600"
              />
              <button
                type="button"
                onClick={() => {
                  setUserModalRole('Student')
                  setShowUsersModal(true)
                }}
                className="rounded-xl bg-indigo-600 px-3.5 py-1.5 text-dash-xs font-bold text-white shadow-card hover:bg-indigo-700 cursor-pointer"
              >
                + Add Student
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-dash-xs border-collapse">
              <thead>
                <tr className="border-b border-line bg-slate-50 text-left font-bold text-slate-600">
                  <th className="p-3">#</th>
                  <th className="p-3">Student Name</th>
                  <th className="p-3">Roll No / ID</th>
                  <th className="p-3">Grade & Section</th>
                  <th className="p-3">Parent Name & Contact</th>
                  <th className="p-3">Active Sessions</th>
                  <th className="p-3">Games Played</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((s, idx) => (
                  <tr key={s.id} className="border-b border-line hover:bg-slate-50">
                    <td className="p-3 font-bold text-muted font-mono">{idx + 1}</td>
                    <td className="p-3 font-extrabold text-ink">{s.name}</td>
                    <td className="p-3 font-mono text-muted">{s.id} (Roll: {s.rollNo})</td>
                    <td className="p-3 font-bold text-slate-700">{s.grade}</td>
                    <td className="p-3">
                      <span className="font-bold text-ink block">{s.parent}</span>
                      <span className="text-[11px] text-muted block">{s.phone}</span>
                    </td>
                    <td className="p-3 font-bold text-indigo-600">{s.activeSessions} sessions</td>
                    <td className="p-3 font-bold text-slate-700">{s.gamesPlayed} games</td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                          s.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: Teachers Tab */}
      {activeTab === 'Teachers' && (
        <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-dash-lg font-extrabold text-ink">Teachers Directory ({filteredTeachers.length})</h2>
              <p className="text-dash-xs text-muted">All active teaching staff and their assigned classes.</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="search"
                placeholder="Search teacher by name or subject..."
                value={teacherSearch}
                onChange={(e) => setTeacherSearch(e.target.value)}
                className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold outline-none w-64 focus:border-indigo-600"
              />
              <button
                type="button"
                onClick={() => {
                  setUserModalRole('Teacher')
                  setShowUsersModal(true)
                }}
                className="rounded-xl bg-indigo-600 px-3.5 py-1.5 text-dash-xs font-bold text-white shadow-card hover:bg-indigo-700 cursor-pointer"
              >
                + Add Teacher
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-dash-xs border-collapse">
              <thead>
                <tr className="border-b border-line bg-slate-50 text-left font-bold text-slate-600">
                  <th className="p-3">#</th>
                  <th className="p-3">Teacher Name</th>
                  <th className="p-3">Subject & Specialty</th>
                  <th className="p-3">Assigned Classes</th>
                  <th className="p-3">Active Sessions Today</th>
                  <th className="p-3">Rating</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.map((t, idx) => (
                  <tr key={t.id} className="border-b border-line hover:bg-slate-50">
                    <td className="p-3 font-bold text-muted font-mono">{idx + 1}</td>
                    <td className="p-3">
                      <span className="font-extrabold text-ink block">{t.name}</span>
                      <span className="text-[11px] text-muted block">{t.email}</span>
                    </td>
                    <td className="p-3 font-bold text-slate-800">{t.subject}</td>
                    <td className="p-3 font-bold text-indigo-600">{t.classes}</td>
                    <td className="p-3 font-bold text-emerald-600">{t.activeSessionsToday} sessions</td>
                    <td className="p-3 font-extrabold text-amber-600">{t.rating}</td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                          t.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: Parents Tab */}
      {activeTab === 'Parents' && (
        <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-dash-lg font-extrabold text-ink">Parents Directory ({filteredParents.length})</h2>
              <p className="text-dash-xs text-muted">All linked parents and communication preferences.</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="search"
                placeholder="Search parent by name or email..."
                value={parentSearch}
                onChange={(e) => setParentSearch(e.target.value)}
                className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold outline-none w-64 focus:border-indigo-600"
              />
              <button
                type="button"
                onClick={() => {
                  setUserModalRole('Parent')
                  setShowUsersModal(true)
                }}
                className="rounded-xl bg-indigo-600 px-3.5 py-1.5 text-dash-xs font-bold text-white shadow-card hover:bg-indigo-700 cursor-pointer"
              >
                + Add Parent Link
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-dash-xs border-collapse">
              <thead>
                <tr className="border-b border-line bg-slate-50 text-left font-bold text-slate-600">
                  <th className="p-3">#</th>
                  <th className="p-3">Parent Name</th>
                  <th className="p-3">Contact Email & Phone</th>
                  <th className="p-3">Linked Children</th>
                  <th className="p-3">Channels</th>
                  <th className="p-3">Last Active</th>
                  <th className="p-3">Link Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredParents.map((p, idx) => (
                  <tr key={p.id} className="border-b border-line hover:bg-slate-50">
                    <td className="p-3 font-bold text-muted font-mono">{idx + 1}</td>
                    <td className="p-3 font-extrabold text-ink">{p.name}</td>
                    <td className="p-3">
                      <span className="font-bold text-slate-800 block">{p.email}</span>
                      <span className="text-[11px] text-muted block">{p.phone}</span>
                    </td>
                    <td className="p-3 font-bold text-indigo-600">{p.children.join(', ')}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 text-[10px] font-extrabold">
                        {p.commEmail && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">Email</span>}
                        {p.commSms && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded">SMS</span>}
                        {p.commWhatsapp && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">WhatsApp</span>}
                      </div>
                    </td>
                    <td className="p-3 text-muted">{p.lastActive}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800">
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 5: Finance Tab (Complete Billing Report) */}
      {activeTab === 'Finance' && (
        <div className="space-y-6">
          {/* Financial Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/*
              🔴 TWO OF THESE FOUR WERE STATEMENTS ABOUT MONEY THAT NOBODY MADE.
              "Next Invoice Date" read **15 Sep 2026** for every school an
              operator opened, and "Payment Gateway Status" read **Razorpay &
              NEFT · Live & Active** — a claim that this deployment can take card
              payments, which `billing.ts` says is a question the server has to
              be ASKED: *"a deployment with no Razorpay credentials is a
              legitimate configuration."* A false green light on a billing screen
              is the one fabrication that costs somebody money.

              ⚠️ ALL FOUR NOW COME FROM THIS SCHOOL'S OWN SUBSCRIPTION, and a
              school with no subscription shows dashes and says so.
            */}
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card">
              <span className="text-dash-xs font-bold text-muted block">Charge per period</span>
              <span className="text-dash-xl font-black text-ink tabular block my-1">
                {subscription === null || subscription.netChargePaise === null
                  ? "—"
                  : `₹${(subscription.netChargePaise / 100).toLocaleString()}`}
              </span>
              <span className="text-dash-xs font-medium text-muted">
                {subscription?.plan?.name ?? "No plan"}
              </span>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card">
              <span className="text-dash-xs font-bold text-muted block">Paid until</span>
              <span className="text-dash-xl font-black text-amber-600 tabular block my-1">
                {subscription === null || subscription.paidUntil === null
                  ? "—"
                  : new Date(subscription.paidUntil).toLocaleDateString()}
              </span>
              <span className="text-dash-xs font-semibold text-muted">
                {subscription?.status ?? "No subscription"}
              </span>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card">
              <span className="text-dash-xs font-bold text-muted block">Renews</span>
              <span className="text-dash-xl font-black text-indigo-600 block my-1">
                {subscription === null || subscription.currentPeriodEnd === null
                  ? "—"
                  : new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </span>
              <span className="text-dash-xs font-semibold text-muted">
                {subscription === null || subscription.trialEndsAt === null
                  ? "End of the current period"
                  : `Trial ends ${new Date(subscription.trialEndsAt).toLocaleDateString()}`}
              </span>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-4 shadow-card">
              <span className="text-dash-xs font-bold text-muted block">Seats billed</span>
              <span className="text-dash-xl font-black text-ink tabular block my-1">
                {tileValue(subscription?.seats ?? null)}
              </span>
              <span className="text-dash-xs font-semibold text-muted">
                {subscription === null || subscription.couponCode === null
                  ? "No discount applied"
                  : `Discount ${subscription.couponCode}`}
              </span>
            </div>
          </div>

          {/* Complete Invoices & Billing History Table */}
          <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-dash text-dash-md font-extrabold text-ink">Billing & Invoice History</h3>
                <p className="text-dash-xs text-muted">All subscription payments and NEFT receipts for {formData.name}.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowSubModal(true)}
                className="rounded-xl bg-indigo-600 px-3.5 py-1.5 text-dash-xs font-bold text-white shadow-card hover:bg-indigo-700 cursor-pointer"
              >
                + Generate Custom Invoice
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-dash-xs border-collapse">
                <thead>
                  <tr className="border-b border-line bg-slate-50 text-left font-bold text-slate-600">
                    <th className="p-3">Invoice ID</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Billing Period</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Payment Method</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {billingInvoices.map((inv) => (
                    <tr key={inv.invoiceId} className="border-b border-line hover:bg-slate-50">
                      <td className="p-3 font-mono font-extrabold text-indigo-600">{inv.invoiceId}</td>
                      <td className="p-3 font-bold text-slate-700">{inv.date}</td>
                      <td className="p-3 text-muted">{inv.period}</td>
                      <td className="p-3 font-black text-ink">{inv.amount}</td>
                      <td className="p-3 font-semibold text-slate-800">{inv.method}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800">
                          {inv.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          className="font-bold text-indigo-600 hover:underline cursor-pointer"
                        >
                          Download Receipt PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 6: Settings Tab */}
      {activeTab === 'Settings' && (
        <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-6">
          <div className="border-b border-line pb-4 flex items-center justify-between">
            <div>
              <h2 className="text-dash-lg font-extrabold text-ink">Institute Settings</h2>
              <p className="text-dash-xs text-muted">Configure domain enforcement, security, academic calendar, and notification preferences for {formData.name}.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSettingsSavedToast(true)
                setTimeout(() => setSettingsSavedToast(false), 3000)
              }}
              className="rounded-xl bg-indigo-600 px-5 py-2 text-dash-xs font-bold text-white shadow-card hover:bg-indigo-700 cursor-pointer"
            >
              Save Settings
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-dash-xs">
            {/* Box 1: Security & Domain */}
            <div className="rounded-xl border border-line bg-white p-5 space-y-4 shadow-xs">
              <h3 className="font-bold text-ink text-dash-sm border-b border-line pb-2">Authentication & Security</h3>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-800 block">Enforce Email Domain</span>
                  <span className="text-[11px] text-muted block">Restrict sign-ins strictly to @{formData.emailDomain}</span>
                </div>
                <input
                  type="checkbox"
                  checked={settingsData.enforceDomain}
                  onChange={(e) => setSettingsData({ ...settingsData, enforceDomain: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-800 block">Mandatory 2FA for Staff</span>
                  <span className="text-[11px] text-muted block">Require 2-factor authentication for teachers & admins</span>
                </div>
                <input
                  type="checkbox"
                  checked={settingsData.requireStaff2FA}
                  onChange={(e) => setSettingsData({ ...settingsData, requireStaff2FA: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 rounded cursor-pointer"
                />
              </div>
            </div>

            {/* Box 2: Notifications */}
            <div className="rounded-xl border border-line bg-white p-5 space-y-4 shadow-xs">
              <h3 className="font-bold text-ink text-dash-sm border-b border-line pb-2">Parent Notification Alerts</h3>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-800 block">Automated Email Progress Reports</span>
                  <span className="text-[11px] text-muted block">Send weekly student activity digest to parents</span>
                </div>
                <input
                  type="checkbox"
                  checked={settingsData.autoParentEmailAlerts}
                  onChange={(e) => setSettingsData({ ...settingsData, autoParentEmailAlerts: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-800 block">SMS Attendance Alerts</span>
                  <span className="text-[11px] text-muted block">Send SMS when student is marked absent</span>
                </div>
                <input
                  type="checkbox"
                  checked={settingsData.autoParentSmsAlerts}
                  onChange={(e) => setSettingsData({ ...settingsData, autoParentSmsAlerts: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 rounded cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: Remaining Tabs */}
      {['Academics', 'Attendance', 'Communication', 'Reports'].includes(activeTab) && (
        <div className="rounded-2xl border border-line bg-surface p-8 shadow-card space-y-4 text-center">
          <div className="h-16 w-16 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto text-dash-2xl font-black">
            <InfoIcon />
          </div>
          <h2 className="text-dash-lg font-extrabold text-ink">
            {activeTab} Management Panel
          </h2>
          <p className="text-dash-xs text-muted max-w-lg mx-auto">
            Viewing real-time {activeTab.toLowerCase()} data for <strong className="text-ink">{formData.name}</strong>. Filter, export, and configure specific parameters.
          </p>
          <div className="pt-4 flex justify-center gap-3">
            <button
              type="button"
              onClick={() => setActiveTab('Overview')}
              className="rounded-xl border border-line bg-white px-4 py-2 text-dash-xs font-bold text-ink hover:bg-slate-50 transition-colors cursor-pointer"
            >
              ← Back to Overview
            </button>
            <button
              type="button"
              className="rounded-xl bg-indigo-600 px-4 py-2 text-dash-xs font-bold text-white shadow-card hover:bg-indigo-700 transition-colors cursor-pointer"
            >
              Export {activeTab} Report
            </button>
          </div>
        </div>
      )}

      {/* MODAL 1: Edit Institute */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl border border-line shadow-2xl max-w-2xl w-full p-4 sm:p-6 space-y-6 font-dash max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-line pb-4">
              <div>
                <h3 className="text-dash-md font-extrabold text-ink">Edit Institute Details</h3>
                <p className="text-dash-xs text-muted">Update primary details, principal info, and location for {formData.name}.</p>
              </div>
              <button type="button" onClick={() => setShowEditModal(false)} className="text-muted hover:text-ink font-bold text-lg cursor-pointer">✕</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-dash-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Institute Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Official Email ID</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Principal Name</label>
                <input
                  type="text"
                  value={formData.principal}
                  onChange={(e) => setFormData({ ...formData, principal: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Contact Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">School Type</label>
                <select
                  value={formData.schoolType}
                  onChange={(e) => setFormData({ ...formData, schoolType: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                >
                  <option>Day School</option>
                  <option>Boarding School</option>
                  <option>Day Boarding</option>
                  <option>International Academy</option>
                </select>
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Board Affiliation</label>
                <input
                  type="text"
                  value={formData.board}
                  onChange={(e) => setFormData({ ...formData, board: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block font-bold text-slate-700 mb-1">Address</label>
                <textarea
                  rows={2}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-line">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-xl border border-line bg-white font-bold text-dash-xs text-ink hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-dash-xs hover:bg-indigo-700 cursor-pointer shadow-card"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Manage Subscription */}
      {showSubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl border border-line shadow-2xl max-w-lg w-full p-4 sm:p-6 space-y-6 font-dash max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-line pb-4">
              <div>
                <h3 className="text-dash-md font-extrabold text-ink">Manage Subscription</h3>
                <p className="text-dash-xs text-muted">Update subscription tier & seat count for {formData.name}.</p>
              </div>
              <button type="button" onClick={() => setShowSubModal(false)} className="text-muted hover:text-ink font-bold text-lg cursor-pointer">✕</button>
            </div>

            <div className="space-y-4 text-dash-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Current Plan Tier</label>
                <select
                  value={formData.subscriptionPlan}
                  onChange={(e) => setFormData({ ...formData, subscriptionPlan: e.target.value })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                >
                  <option>Premium Annual (Unlimited Seats)</option>
                  <option>Standard Annual (2,500 Seats)</option>
                  <option>Basic Tier (1,000 Seats)</option>
                  <option>Free Trial</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Student Seats Capacity</label>
                <input
                  type="number"
                  value={formData.seatAllocated}
                  onChange={(e) => setFormData({ ...formData, seatAllocated: Number(e.target.value) })}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>

              <div className="rounded-xl bg-slate-50 p-4 border border-line space-y-2">
                <div className="flex justify-between font-bold text-ink">
                  <span>Student Seats Allocated</span>
                  <span className="text-indigo-600 font-extrabold">2,456 / {formData.seatAllocated}</span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${Math.min(100, (2456 / formData.seatAllocated) * 100)}%` }} />
                </div>
                <span className="text-[11px] text-muted block">Renewal Date: 20 July 2026</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-line">
              <button
                type="button"
                onClick={() => setShowSubModal(false)}
                className="px-4 py-2 rounded-xl border border-line bg-white font-bold text-dash-xs text-ink hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowSubModal(false)}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-dash-xs hover:bg-indigo-700 cursor-pointer shadow-card"
              >
                Update Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Manage Users */}
      {showUsersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl border border-line shadow-2xl max-w-xl w-full p-4 sm:p-6 space-y-6 font-dash max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-line pb-4">
              <div>
                <h3 className="text-dash-md font-extrabold text-ink">Manage Institute Users</h3>
                <p className="text-dash-xs text-muted">Add or manage students, teachers, and parents for {formData.name}.</p>
              </div>
              <button type="button" onClick={() => setShowUsersModal(false)} className="text-muted hover:text-ink font-bold text-lg cursor-pointer">✕</button>
            </div>

            {/* Role Switcher Tabs inside Modal */}
            <div className="flex items-center gap-2 border-b border-line pb-2 text-dash-xs font-bold">
              {(['Student', 'Teacher', 'Parent'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setUserModalRole(r)}
                  className={`px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
                    userModalRole === r ? 'bg-indigo-600 text-white font-extrabold' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  + Add {r}
                </button>
              ))}
            </div>

            <div className="space-y-4 text-dash-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">{userModalRole} Full Name</label>
                <input
                  type="text"
                  placeholder={`Enter ${userModalRole.toLowerCase()} name...`}
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Email / ID</label>
                <input
                  type="email"
                  placeholder={`Enter ${userModalRole.toLowerCase()} email...`}
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>

              {userModalRole === 'Student' && (
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Assigned Grade & Section</label>
                  <select
                    value={newUserGrade}
                    onChange={(e) => setNewUserGrade(e.target.value)}
                    className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                  >
                    <option>Class 1A</option>
                    <option>Class 2A</option>
                    <option>Class 3A</option>
                    <option>Class 4B</option>
                    <option>Class 5A</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-line">
              <button
                type="button"
                onClick={() => setShowUsersModal(false)}
                className="px-4 py-2 rounded-xl border border-line bg-white font-bold text-dash-xs text-ink hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUsersModal(false)
                  setNewUserName('')
                  setNewUserEmail('')
                }}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-dash-xs hover:bg-indigo-700 cursor-pointer shadow-card"
              >
                Save {userModalRole}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: Communication Broadcast */}
      {showCommModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl border border-line shadow-2xl max-w-xl w-full p-4 sm:p-6 space-y-6 font-dash max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-line pb-4">
              <div>
                <h3 className="text-dash-md font-extrabold text-ink">Broadcast Communication</h3>
                <p className="text-dash-xs text-muted">Send announcement or notice to {formData.name}.</p>
              </div>
              <button type="button" onClick={() => setShowCommModal(false)} className="text-muted hover:text-ink font-bold text-lg cursor-pointer">✕</button>
            </div>

            <div className="space-y-4 text-dash-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Recipients</label>
                <select
                  value={commRecipient}
                  onChange={(e) => setCommRecipient(e.target.value)}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                >
                  <option>All Staff & Parents</option>
                  <option>Teachers Only</option>
                  <option>Parents Only</option>
                  <option>School Principal & Admin</option>
                </select>
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Notice Subject</label>
                <input
                  type="text"
                  placeholder="e.g. Platform System Maintenance & Schedule"
                  value={commSubject}
                  onChange={(e) => setCommSubject(e.target.value)}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Message Content</label>
                <textarea
                  rows={4}
                  placeholder="Write your announcement details here..."
                  value={commBody}
                  onChange={(e) => setCommBody(e.target.value)}
                  className="w-full rounded-xl border border-line p-2.5 font-bold focus:border-indigo-600 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-line">
              <button
                type="button"
                onClick={() => setShowCommModal(false)}
                className="px-4 py-2 rounded-xl border border-line bg-white font-bold text-dash-xs text-ink hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCommModal(false)
                  setCommSentToast(true)
                  setTimeout(() => setCommSentToast(false), 4000)
                }}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-dash-xs hover:bg-indigo-700 cursor-pointer shadow-card"
              >
                Send Broadcast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 5: Deactivate / Reactivate Institute */}
      {showDeactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl border border-line shadow-2xl max-w-md w-full p-4 sm:p-6 space-y-6 font-dash max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="h-10 w-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                <AlertIcon size={24} />
              </div>
              <div>
                <h3 className="text-dash-md font-extrabold text-ink">
                  {status === 'Active' ? 'Deactivate Institute?' : 'Reactivate Institute?'}
                </h3>
                <p className="text-dash-xs text-muted">
                  {status === 'Active'
                    ? `Deactivating ${formData.name} will suspend staff & student sign-in until re-enabled.`
                    : `Reactivating ${formData.name} will restore all portal access for students & staff.`}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-line">
              <button
                type="button"
                onClick={() => setShowDeactivateModal(false)}
                className="px-4 py-2 rounded-xl border border-line bg-white font-bold text-dash-xs text-ink hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatus(status === 'Active' ? 'Inactive' : 'Active')
                  setShowDeactivateModal(false)
                }}
                className={`px-4 py-2 rounded-xl font-bold text-dash-xs text-white shadow-card cursor-pointer ${
                  status === 'Active' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                Confirm {status === 'Active' ? 'Deactivation' : 'Reactivation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
